import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { publicationContext } from "../context/CreatePublicationContext";
import SearchBar from "./SearchBar";
import BookData from "../Data.json";
import { useTranslation } from "react-i18next";
import { MdArrowForwardIos } from "react-icons/md";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import axios from "axios";
import API_BASE_URL from "../config/api";

/* ── Petit composant titre de section réutilisable ── */
const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-bold tracking-widest uppercase text-gray-700 pb-2 mb-4 border-b-2 border-red-600 w-fit">
    {children}
  </h2>
);

const RightPageComponants = () => {
  const toast = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setPublicationUnikId } = useContext(publicationContext);

  const [newsLetterEmail, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [listPublications, setListPublication] = useState([]);
  const [ListOfEvent, setListOfEvent] = useState([]);

  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsValidEmail(validateEmail(e.target.value));
  };

  const subscribeNewsletter = (e) => {
    e.preventDefault();
    if (!isValidEmail) {
      alert("Adresse e-mail invalide. Veuillez saisir une adresse e-mail valide.");
      return;
    }
    axios
      .post(`${API_BASE_URL}/sendmail/newsLetter`, { newsLetterEmail })
      .then((res) => {
        if (res.data.error) {
          toast.current.show({
            severity: "error",
            summary: "Erreur d'abonnement",
            detail: "Cette adresse e-mail est déjà inscrite à la newsletter.",
          });
        } else {
          toast.current.show({
            severity: "success",
            summary: "Abonnement réussi",
            detail: "Nous vous recontacterons bientôt !",
          });
          setEmail("");
        }
      })
      .catch(() => alert("Vous êtes déjà abonné(e) à la newsletter de GS7S."));
  };

  useEffect(() => {
    axios.get(`${API_BASE_URL}/publications/`).then((res) => setListPublication(res.data));
    axios.get(`${API_BASE_URL}/event`).then((res) => setListOfEvent(res.data));
  }, []);

  const sortedPublications = [...listPublications].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return (
    <div className="flex flex-col gap-5 px-1">
      <Toast ref={toast} />

      {/* ── Recherche ── */}
      <div className="w-full">
        <SearchBar
          placeholder="Rechercher..."
          data={BookData}
          listPublications={listPublications}
        />
      </div>

      {/* ── Articles récents ── */}
      <div className="bg-gray-100 rounded-xl p-5 border border-gray-200">
        <SectionTitle>{t("Articles récents")}</SectionTitle>
        <ul className="flex flex-col divide-y divide-gray-200">
          {sortedPublications.slice(0, 5).map((pub) => (
            <li key={pub._id}>
              <button
                onClick={() => {
                  setPublicationUnikId(pub._id);
                  localStorage.setItem("publicationUnikId", pub._id);
                  navigate(`/publications/${pub.publicationUrl}`);
                }}
                className="w-full text-left py-3 flex items-start gap-2 group"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 group-hover:scale-125 transition-transform duration-200" />
                <span className="text-gray-700 text-base leading-snug group-hover:text-red-600 transition-colors duration-200 font-medium">
                  {pub.title}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button
          onClick={() => navigate("/publications")}
          className="mt-4 text-xs font-semibold text-red-600 hover:text-red-500 flex items-center gap-1 transition-colors duration-200 uppercase tracking-wide"
        >
          Voir toutes les publications <MdArrowForwardIos size={11} />
        </button>
      </div>

      {/* ── Prochains événements ── */}
      <div className="bg-gray-100 rounded-xl p-5 border border-gray-200">
        <SectionTitle>{t("Prochains événements")}</SectionTitle>
        {ListOfEvent.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            {t("Pas d'événement prévu pour le moment.")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {ListOfEvent.map((event) => {
              const dateStart = new Date(event.start).toLocaleDateString("fr-FR");
              const dateEnd = new Date(event.end).toLocaleDateString("fr-FR");
              return (
                <div
                  key={event._id}
                  className="border-l-2 border-red-600 pl-3 py-1 hover:border-red-400 transition-colors duration-200"
                >
                  <p className="text-gray-700 text-sm font-medium leading-snug mb-1">
                    {event.title}
                  </p>
                  <p className="text-gray-500 text-xs">
                    <span className="text-red-600 font-semibold">Du</span> {dateStart}{" "}
                    <span className="text-red-600 font-semibold">au</span> {dateEnd}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Newsletter ── */}
      <div className="bg-gray-100 rounded-xl p-5 border border-gray-200">
        <SectionTitle>{t("Newsletter")}</SectionTitle>
        <p className="text-gray-500 text-xs mb-4 leading-relaxed">
          Abonnez-vous pour recevoir nos dernières actualités.
        </p>
        <form onSubmit={subscribeNewsletter} className="flex flex-col gap-3">
          <InputText
            placeholder="Votre adresse e-mail"
            className={`w-full bg-white text-gray-800 text-sm rounded-lg px-4 py-2.5 placeholder-gray-400 outline-none transition-colors duration-200 ${
              isValidEmail
                ? "border border-gray-300 focus:border-red-500"
                : "border border-red-500"
            }`}
            value={newsLetterEmail}
            onChange={handleEmailChange}
            required
          />
          <button
            type="submit"
            className="bg-red-600 hover:bg-red-700 active:scale-95 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 w-full"
          >
            {t("S'abonner")} <MdArrowForwardIos size={13} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default RightPageComponants;